/**
 * Service for managing alternative career ranks in Rogue Trader
 *
 * Alternative career ranks allow characters to take specialized paths
 * that replace standard career progression for one rank level.
 *
 * This service provides methods for:
 * - Retrieving alternative ranks by career
 * - Filtering alternative ranks by minimum rank requirement
 * - Validating prerequisites against character data
 * - Getting detailed prerequisite status for display
 *
 * Requirements: 3.1, 3.2, 3.3, 4.1, 4.2, 4.3, 4.4, 4.5
 */

import type { Rank } from '../types/advancements';
import type { Character, Characteristics, SkillTraining } from '../types/character';
import type {
  AlternativeRank,
  AlternativeRankPrerequisite,
  AlternativeRankFile,
  PrerequisiteCheckResult,
  PrerequisiteStatus
} from '../types/alternativeRanks';

/**
 * Mapping from display characteristic names to Character property keys
 * Used to look up characteristic values from prerequisites
 */
const CHARACTERISTIC_NAME_MAP: Record<string, keyof Characteristics> = {
  'Weapon Skill': 'weaponSkill',
  'Ballistic Skill': 'ballisticSkill',
  Strength: 'strength',
  Toughness: 'toughness',
  Agility: 'agility',
  Intelligence: 'intelligence',
  Perception: 'perception',
  Willpower: 'willpower',
  Fellowship: 'fellowship',
  Influence: 'influence',
  // Also support abbreviated forms commonly used in prerequisites
  WS: 'weaponSkill',
  BS: 'ballisticSkill',
  S: 'strength',
  T: 'toughness',
  Ag: 'agility',
  Int: 'intelligence',
  Per: 'perception',
  WP: 'willpower',
  Fel: 'fellowship',
  Inf: 'influence'
};

/**
 * Skill training level hierarchy for comparison
 * Higher index = higher training level
 */
const SKILL_TRAINING_HIERARCHY: SkillTraining[] = [
  'untrained' as SkillTraining,
  'trained' as SkillTraining,
  'experienced' as SkillTraining,
  'adept' as SkillTraining
];

/**
 * Get the numeric index of a skill training level for comparison
 * @param training - The skill training level
 * @returns The index in the hierarchy (higher = better)
 */
function getTrainingIndex(training: SkillTraining | string): number {
  const normalizedTraining = training.toLowerCase() as SkillTraining;
  const index = SKILL_TRAINING_HIERARCHY.indexOf(normalizedTraining);
  return index >= 0 ? index : -1;
}

/**
 * Service class for alternative career rank operations
 *
 * All methods are static as this service operates on static data
 * and does not maintain internal state.
 */
export class AlternativeRankService {
  /**
   * Cached alternative ranks loaded from individual files
   * Null indicates not yet loaded
   */
  private static alternativeRanks: AlternativeRank[] | null = null;

  /**
   * Load all alternative rank files from alt-advances folder
   *
   * Uses Vite's import.meta.glob to dynamically import all JSON files
   * from the alt-advances directory. Transforms the file format to
   * runtime AlternativeRank format and caches the result.
   *
   * @returns Array of all alternative ranks
   * @throws Error if any file fails validation
   *
   * Requirements: 6.1, 9.1, 9.2, 9.3
   */
  private static loadAlternativeRanks(): AlternativeRank[] {
    // Return cached data if already loaded
    if (this.alternativeRanks !== null) {
      return this.alternativeRanks;
    }

    // Import all JSON files from alt-advances folder
    // eager: true loads them synchronously at build time
    const files = import.meta.glob<AlternativeRankFile>('../data/alt-advances/*.json', {
      eager: true,
      import: 'default'
    });

    const ranks: AlternativeRank[] = [];

    // Transform each file to runtime AlternativeRank format
    for (const [path, fileData] of Object.entries(files)) {
      try {
        // Transform AlternativeRankFile format to runtime AlternativeRank format
        const rank: AlternativeRank = {
          id: fileData.altRankInfo.id,
          name: fileData.altRankInfo.name,
          description: fileData.altRankInfo.description,
          career: fileData.altRankInfo.career,
          careerId: fileData.altRankInfo.careerId,
          minimumRank: fileData.altRankInfo.minimumRank,
          prerequisites: fileData.altRankInfo.prerequisites,
          advancements: fileData.advancements,
          grantedTalents: fileData.altRankInfo.grantedTalents,
          grantedTraits: fileData.altRankInfo.grantedTraits,
          rulebook: fileData.altRankInfo.rulebook
        };

        ranks.push(rank);
      } catch (error) {
        // Re-throw validation errors with context
        if (error instanceof Error) {
          throw new Error(`Failed to load alternative rank from ${path}: ${error.message}`);
        }
        throw new Error(`Failed to load alternative rank from ${path}: Unknown error`);
      }
    }

    // Cache the loaded ranks
    this.alternativeRanks = ranks;
    return ranks;
  }

  /**
   * Get all alternative ranks for a career
   *
   * Filters the alternative ranks data to return only those
   * available to the specified career. Includes alternative ranks
   * with careerId "any" which are available to all careers.
   *
   * @param careerId - The kebab-case career ID (e.g., "seneschal")
   * @returns Array of alternative ranks for the career
   *
   * Requirements: 3.2
   */
  static getAlternativeRanksForCareer(careerId: string): AlternativeRank[] {
    const ranks = this.loadAlternativeRanks();
    return ranks.filter((rank) => rank.careerId === careerId || rank.careerId === 'any');
  }

  /**
   * Get alternative ranks available at a specific rank level
   *
   * Filters alternative ranks by both career and minimum rank requirement.
   * Only returns ranks where the character's rank meets or exceeds
   * the alternative rank's minimumRank.
   *
   * Includes alternative ranks with careerId "any" which are available to all careers.
   *
   * @param careerId - The kebab-case career ID (e.g., "seneschal")
   * @param rank - The character's current rank (1-8)
   * @returns Array of alternative ranks available at this rank level
   *
   * Requirements: 3.3
   */
  static getAlternativeRanksAtLevel(careerId: string, rank: Rank): AlternativeRank[] {
    const ranks = this.loadAlternativeRanks();
    return ranks.filter(
      (altRank) =>
        (altRank.careerId === careerId || altRank.careerId === 'any') && altRank.minimumRank <= rank
    );
  }

  /**
   * Check if character meets all prerequisites for an alternative rank
   *
   * Validates each prerequisite against the character's current state:
   * - Skill prerequisites: Check if character has the skill at required training level
   * - Talent prerequisites: Check if character has the talent
   * - Characteristic prerequisites: Check if characteristic value meets minimum
   *
   * @param alternativeRank - The alternative rank to check prerequisites for
   * @param character - The character to validate against
   * @returns Result indicating if all prerequisites are met and which are unmet
   *
   * Requirements: 4.1, 4.2, 4.3
   */
  static meetsPrerequisites(
    alternativeRank: AlternativeRank,
    character: Character
  ): PrerequisiteCheckResult {
    const unmetPrerequisites: AlternativeRankPrerequisite[] = [];

    // If no prerequisites, all are met
    if (!alternativeRank.prerequisites || alternativeRank.prerequisites.length === 0) {
      return { met: true, unmetPrerequisites: [] };
    }

    for (const prereq of alternativeRank.prerequisites) {
      const isMet = this.checkSinglePrerequisite(prereq, character);
      if (!isMet) {
        unmetPrerequisites.push(prereq);
      }
    }

    return {
      met: unmetPrerequisites.length === 0,
      unmetPrerequisites
    };
  }

  /**
   * Check if a single prerequisite is met by the character
   *
   * @param prereq - The prerequisite to check
   * @param character - The character to validate against
   * @returns true if the prerequisite is met, false otherwise
   */
  private static checkSinglePrerequisite(
    prereq: AlternativeRankPrerequisite,
    character: Character
  ): boolean {
    switch (prereq.type) {
      case 'skill':
        return this.checkSkillPrerequisite(prereq, character);
      case 'talent':
        return this.checkTalentPrerequisite(prereq, character);
      case 'characteristic':
        return this.checkCharacteristicPrerequisite(prereq, character);
      case 'any-of':
        // "One of the following..." — met as soon as any alternative is met.
        return (prereq.anyOf ?? []).some((alternative) =>
          this.checkSinglePrerequisite(alternative, character)
        );
      case 'other':
        // "Other" type prerequisites require manual verification
        // Return false by default - UI will allow manual override
        return false;
      default:
        // Unknown prerequisite type - treat as not met
        return false;
    }
  }

  /**
   * Check if character has a skill at the required training level
   *
   * Skill matching is case-insensitive and supports both exact matches
   * and partial matches for specialist skills (e.g., "Common Lore (Underworld)")
   *
   * @param prereq - The skill prerequisite
   * @param character - The character to check
   * @returns true if the character has the skill at required level or higher
   *
   * Requirements: 4.1
   */
  private static checkSkillPrerequisite(
    prereq: AlternativeRankPrerequisite,
    character: Character
  ): boolean {
    if (!character.skills || character.skills.length === 0) {
      return false;
    }

    const requiredName = prereq.name.toLowerCase();
    const requiredTraining = prereq.training || ('trained' as SkillTraining);

    // Find matching skill - check both exact name match and specialist skill match
    const matchingSkill = character.skills.find((skill) => {
      const skillName = skill.name.toLowerCase();

      // Exact match
      if (skillName === requiredName) {
        return true;
      }

      // For specialist skills, also check with specialization
      // e.g., "Common Lore (Underworld)" should match skill with name "Common Lore" and specialization "Underworld"
      if (skill.isSpecialist && skill.specialization) {
        const fullName = `${skill.name} (${skill.specialization})`.toLowerCase();
        if (fullName === requiredName) {
          return true;
        }
      }

      return false;
    });

    if (!matchingSkill) {
      return false;
    }

    // Check if training level meets requirement
    const characterTrainingIndex = getTrainingIndex(matchingSkill.training);
    const requiredTrainingIndex = getTrainingIndex(requiredTraining);

    // An unrecognised required level is a data error. It must not be treated as
    // satisfied: getTrainingIndex returns -1, and every real character level is
    // >= 0, so comparing directly would silently pass for an untrained
    // character. Flight Marshal carried training "+10", which did exactly that.
    if (requiredTrainingIndex < 0) {
      console.error(
        `Unrecognised training level "${requiredTraining}" on skill prerequisite "${prereq.name}" — ` +
          `expected one of ${SKILL_TRAINING_HIERARCHY.join(', ')}`
      );
      return false;
    }

    // Character's training must be >= required training
    return characterTrainingIndex >= requiredTrainingIndex;
  }

  /**
   * Check if character has a required talent
   *
   * Talent matching is case-insensitive and supports both exact matches
   * and partial matches for specialized talents (e.g., "Peer (Underworld)")
   *
   * @param prereq - The talent prerequisite
   * @param character - The character to check
   * @returns true if the character has the talent
   *
   * Requirements: 4.2
   */
  private static checkTalentPrerequisite(
    prereq: AlternativeRankPrerequisite,
    character: Character
  ): boolean {
    if (!character.talents || character.talents.length === 0) {
      return false;
    }

    const requiredName = prereq.name.toLowerCase();

    // Find matching talent
    const hasTalent = character.talents.some((talent) => {
      const talentName = talent.name.toLowerCase();

      // Exact match
      if (talentName === requiredName) {
        return true;
      }

      // For specialized talents, also check with specialization
      // e.g., "Peer (Underworld)" should match talent with name "Peer" and specialization "Underworld"
      if (talent.specialization) {
        const fullName = `${talent.name} (${talent.specialization})`.toLowerCase();
        if (fullName === requiredName) {
          return true;
        }
      }

      return false;
    });

    return hasTalent;
  }

  /**
   * Check if character's characteristic meets the required value
   *
   * Supports both full names (e.g., "Toughness") and abbreviations (e.g., "T")
   *
   * @param prereq - The characteristic prerequisite
   * @param character - The character to check
   * @returns true if the character's characteristic value >= required value
   *
   * Requirements: 4.3
   */
  private static checkCharacteristicPrerequisite(
    prereq: AlternativeRankPrerequisite,
    character: Character
  ): boolean {
    if (!character.characteristics || prereq.value === undefined) {
      return false;
    }

    // Map the prerequisite name to the character property key
    const characteristicKey = CHARACTERISTIC_NAME_MAP[prereq.name];

    if (!characteristicKey) {
      // Unknown characteristic name - try case-insensitive lookup
      const normalizedName = prereq.name.trim();
      const foundKey = Object.entries(CHARACTERISTIC_NAME_MAP).find(
        ([key]) => key.toLowerCase() === normalizedName.toLowerCase()
      );

      if (!foundKey) {
        return false;
      }

      const characterValue = character.characteristics[foundKey[1]];
      return typeof characterValue === 'number' && characterValue >= prereq.value;
    }

    const characterValue = character.characteristics[characteristicKey];
    return typeof characterValue === 'number' && characterValue >= prereq.value;
  }

  /**
   * Get detailed prerequisite status for display
   *
   * Returns the status of each prerequisite with additional context
   * for UI display, including what the character currently has.
   *
   * @param alternativeRank - The alternative rank to check prerequisites for
   * @param character - The character to validate against
   * @returns Array of prerequisite statuses with met/unmet and current values
   *
   * Requirements: 4.4, 4.5
   */
  static getPrerequisiteStatus(
    alternativeRank: AlternativeRank,
    character: Character
  ): PrerequisiteStatus[] {
    // If no prerequisites, return empty array
    if (!alternativeRank.prerequisites || alternativeRank.prerequisites.length === 0) {
      return [];
    }

    return alternativeRank.prerequisites.map((prereq) => {
      const met = this.checkSinglePrerequisite(prereq, character);
      const currentValue = this.getCurrentValueForPrerequisite(prereq, character);

      return {
        prerequisite: prereq,
        met,
        currentValue
      };
    });
  }

  /**
   * Get the character's current value for a prerequisite for display
   *
   * @param prereq - The prerequisite to get the current value for
   * @param character - The character to check
   * @returns A string describing the character's current state for this prerequisite
   */
  private static getCurrentValueForPrerequisite(
    prereq: AlternativeRankPrerequisite,
    character: Character
  ): string {
    switch (prereq.type) {
      case 'skill':
        return this.getSkillCurrentValue(prereq, character);
      case 'talent':
        return this.getTalentCurrentValue(prereq, character);
      case 'characteristic':
        return this.getCharacteristicCurrentValue(prereq, character);
      case 'any-of': {
        // Name whichever alternative the character already satisfies.
        const satisfied = (prereq.anyOf ?? []).find((alternative) =>
          this.checkSinglePrerequisite(alternative, character)
        );
        return satisfied ? satisfied.name : 'None of the alternatives';
      }
      case 'other':
        // "Other" type prerequisites require manual verification
        return 'Manual verification required';
      default:
        return 'Unknown';
    }
  }

  /**
   * Get the character's current skill training level for display
   *
   * @param prereq - The skill prerequisite
   * @param character - The character to check
   * @returns A string describing the current training level (e.g., "Trained", "Untrained")
   */
  private static getSkillCurrentValue(
    prereq: AlternativeRankPrerequisite,
    character: Character
  ): string {
    if (!character.skills || character.skills.length === 0) {
      return 'Untrained';
    }

    const requiredName = prereq.name.toLowerCase();

    // Find matching skill - check both exact name match and specialist skill match
    const matchingSkill = character.skills.find((skill) => {
      const skillName = skill.name.toLowerCase();

      // Exact match
      if (skillName === requiredName) {
        return true;
      }

      // For specialist skills, also check with specialization
      if (skill.isSpecialist && skill.specialization) {
        const fullName = `${skill.name} (${skill.specialization})`.toLowerCase();
        if (fullName === requiredName) {
          return true;
        }
      }

      return false;
    });

    if (!matchingSkill) {
      return 'Untrained';
    }

    // Capitalize the training level for display
    const training = matchingSkill.training.toString();
    return training.charAt(0).toUpperCase() + training.slice(1);
  }

  /**
   * Get whether the character has a talent for display
   *
   * @param prereq - The talent prerequisite
   * @param character - The character to check
   * @returns "Has talent" if the character has it, "Missing" otherwise
   */
  private static getTalentCurrentValue(
    prereq: AlternativeRankPrerequisite,
    character: Character
  ): string {
    if (!character.talents || character.talents.length === 0) {
      return 'Missing';
    }

    const requiredName = prereq.name.toLowerCase();

    // Find matching talent
    const hasTalent = character.talents.some((talent) => {
      const talentName = talent.name.toLowerCase();

      // Exact match
      if (talentName === requiredName) {
        return true;
      }

      // For specialized talents, also check with specialization
      if (talent.specialization) {
        const fullName = `${talent.name} (${talent.specialization})`.toLowerCase();
        if (fullName === requiredName) {
          return true;
        }
      }

      return false;
    });

    return hasTalent ? 'Has talent' : 'Missing';
  }

  /**
   * Get the character's current characteristic value for display
   *
   * @param prereq - The characteristic prerequisite
   * @param character - The character to check
   * @returns The current characteristic value as a string (e.g., "35")
   */
  private static getCharacteristicCurrentValue(
    prereq: AlternativeRankPrerequisite,
    character: Character
  ): string {
    if (!character.characteristics) {
      return '0';
    }

    // Map the prerequisite name to the character property key
    let characteristicKey = CHARACTERISTIC_NAME_MAP[prereq.name];

    if (!characteristicKey) {
      // Unknown characteristic name - try case-insensitive lookup
      const normalizedName = prereq.name.trim();
      const foundKey = Object.entries(CHARACTERISTIC_NAME_MAP).find(
        ([key]) => key.toLowerCase() === normalizedName.toLowerCase()
      );

      if (!foundKey) {
        return '0';
      }

      characteristicKey = foundKey[1];
    }

    const characterValue = character.characteristics[characteristicKey];
    return typeof characterValue === 'number' ? characterValue.toString() : '0';
  }

  /**
   * Get all alternative ranks from the data file
   *
   * Utility method to access the raw alternative ranks data.
   *
   * @returns Array of all alternative ranks
   */
  static getAllAlternativeRanks(): AlternativeRank[] {
    return this.loadAlternativeRanks();
  }

  /**
   * Get an alternative rank by its ID
   *
   * @param id - The unique identifier of the alternative rank
   * @returns The alternative rank if found, undefined otherwise
   */
  static getAlternativeRankById(id: string): AlternativeRank | undefined {
    const ranks = this.loadAlternativeRanks();
    return ranks.find((rank) => rank.id === id);
  }
}
